import { useState } from "react";
import { Sparkles, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export const AiStudioTab = () => {
  const [prompt, setPrompt] = useState("");
  const [kind, setKind] = useState("banner");
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState([]);

  const generate = async () => {
    if (!prompt.trim()) {
      toast.error("Describe the image you want");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/generate-image", { prompt, kind });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setResults((r) => [{ url, prompt, kind }, ...r]);
      toast.success("Image generated — ready to use");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const copy = (url) => navigator.clipboard.writeText(url).then(() => toast.success("Image URL copied"));

  return (
    <div className="max-w-4xl" data-testid="ai-studio-tab">
      <p className="font-serif text-2xl text-zinc-900">AI Photo Studio</p>
      <p className="text-sm text-zinc-500 mt-2 mb-10 leading-relaxed">
        Describe the shot — get a studio-grade image. Copy its URL into any banner, brand or product form.
      </p>
      <div className="border border-zinc-200 p-6 space-y-5">
        <div className="flex flex-wrap gap-3">
          {[["banner", "Banner (wide)"], ["product", "Product shot"]].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setKind(v)}
              data-testid={`studio-kind-${v}`}
              className={`text-[10px] uppercase tracking-[0.25em] px-5 py-2.5 border transition-colors duration-300 ${kind === v ? "bg-gold text-white border-gold" : "border-zinc-200 text-zinc-500 hover:border-gold/50 hover:text-gold"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <textarea
          rows={3}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={kind === "banner" ? "e.g. Rose petals and a glowing serum bottle on pink silk, morning light" : "e.g. A matte pink jar of night cream beside fresh rose petals"}
          data-testid="studio-prompt"
          className="w-full bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3 focus:outline-none focus:border-gold/60 transition-colors duration-300"
        />
        <button
          onClick={generate}
          disabled={generating}
          data-testid="studio-generate-btn"
          className="flex items-center gap-2.5 bg-gold text-white text-xs uppercase tracking-[0.3em] px-8 py-4 hover:bg-champagne transition-colors duration-300 disabled:opacity-50"
        >
          {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
          {generating ? "Creating… (10–30s)" : "Generate Image"}
        </button>
      </div>

      {results.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-10">
          {results.map((r, i) => (
            <div key={i} className="border border-zinc-200" data-testid={`studio-result-${i}`}>
              <img src={r.url} alt={r.prompt} className="w-full aspect-video object-cover" />
              <div className="p-4 flex items-center gap-3">
                <p className="text-xs text-zinc-500 flex-1 truncate">{r.prompt}</p>
                <button onClick={() => copy(r.url)} data-testid={`studio-copy-${i}`} className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-gold hover:text-champagne transition-colors duration-200">
                  <Copy size={12} /> Copy URL
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
